import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PIPELINE } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Users, Search, Tag, LayoutGrid, List, Building2, UserPlus } from "lucide-react";
import { Widget, PageHeader, KpiStrip } from "@/components/Widget";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import Loader from "@/components/Loader";

const TYPE_LABEL = { prospect: "Prospect", client: "Client", fournisseur: "Fournisseur" };

export default function Contacts() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("liste");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", type: "prospect", kind: "particulier", siret: "", vat_number: "", company_name: "", tags: "", notes: "" });
  const [dragId, setDragId] = useState(null);
  const [coResults, setCoResults] = useState([]);
  const [coSearching, setCoSearching] = useState(false);
  const coTimer = useRef(null);

  const load = () => api.get("/contacts").then(({ data }) => setContacts(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) return toast.error("Le nom est requis");
    await api.post("/contacts", { ...form, tags: form.tags ? form.tags.split(",").map((t) => t.trim()) : [] });
    toast.success("Contact créé");
    setOpen(false);
    setForm({ name: "", email: "", phone: "", address: "", type: "prospect", kind: "particulier", siret: "", vat_number: "", company_name: "", tags: "", notes: "" });
    setCoResults([]);
    load();
  };

  const searchCompany = (v) => {
    setForm((f) => ({ ...f, name: v }));
    if (coTimer.current) clearTimeout(coTimer.current);
    if ((v || "").trim().length < 2) { setCoResults([]); return; }
    coTimer.current = setTimeout(async () => {
      setCoSearching(true);
      try { const { data } = await api.get("/company-search", { params: { q: v } }); setCoResults(data || []); }
      catch { setCoResults([]); }
      finally { setCoSearching(false); }
    }, 350);
  };
  const pickCompany = (co) => {
    setForm((f) => ({ ...f, name: co.name, company_name: co.name, siret: co.siret || "", vat_number: co.vat_number || "", address: co.address || f.address }));
    setCoResults([]);
  };

  const onDrop = async (stage) => {
    if (!dragId) return;
    setContacts((cs) => cs.map((c) => (c.id === dragId ? { ...c, pipeline_stage: stage } : c)));
    await api.patch(`/contacts/${dragId}/stage`, { pipeline_stage: stage });
    setDragId(null);
  };

  const filtered = contacts.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()) || (c.email || "").includes(q));

  return (
    <div className="space-y-6" data-testid="contacts-page">
      <PageHeader title="Contacts" subtitle={`${contacts.length} contact(s) dans votre CRM.`} actions={<>
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="rounded-full bg-zinc-900">
              <TabsTrigger value="liste" data-testid="view-list" className="rounded-full data-[state=active]:bg-[#FF6A00] data-[state=active]:text-white"><List className="mr-1 h-4 w-4" />Liste</TabsTrigger>
              <TabsTrigger value="pipeline" data-testid="view-kanban" className="rounded-full data-[state=active]:bg-[#FF6A00] data-[state=active]:text-white"><LayoutGrid className="mr-1 h-4 w-4" />Pipeline</TabsTrigger>
            </TabsList>
          </Tabs>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-contact-btn" className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Plus className="mr-1 h-4 w-4" />Contact</Button>
            </DialogTrigger>
            <DialogContent className="border-zinc-800 bg-zinc-950">
              <DialogHeader><DialogTitle className="text-white">Nouveau contact</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="flex gap-2" data-testid="contact-kind-toggle">
                  <button type="button" data-testid="contact-kind-particulier" onClick={() => setForm({ ...form, kind: "particulier", siret: "", vat_number: "", company_name: "" })} className={`flex-1 rounded-full border px-3 py-2 text-sm font-medium transition-colors ${form.kind !== "pro" ? "border-[#FF6A00] bg-[#FF6A00]/10 text-[#FF6A00]" : "border-zinc-800 text-zinc-400 hover:text-white"}`}>Particulier</button>
                  <button type="button" data-testid="contact-kind-pro" onClick={() => setForm({ ...form, kind: "pro" })} className={`flex-1 rounded-full border px-3 py-2 text-sm font-medium transition-colors ${form.kind === "pro" ? "border-[#FF6A00] bg-[#FF6A00]/10 text-[#FF6A00]" : "border-zinc-800 text-zinc-400 hover:text-white"}`}>Professionnel (SIRET)</button>
                </div>
                {form.kind === "pro" ? (
                  <div className="relative">
                    <Label className="text-zinc-400">Entreprise *</Label>
                    <Input data-testid="contact-company-search" className="mt-1 border-zinc-800 bg-zinc-900" placeholder="Tapez le nom — recherche dans la base nationale…" value={form.name} onChange={(e) => searchCompany(e.target.value)} />
                    {coSearching && <p className="mt-1 text-xs text-zinc-500" data-testid="contact-company-loading">Recherche…</p>}
                    {coResults.length > 0 && (
                      <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-zinc-800 bg-zinc-900 shadow-xl" data-testid="contact-company-results">
                        {coResults.map((co) => (
                          <button type="button" key={co.siret || co.siren} data-testid={`contact-company-opt-${co.siret || co.siren}`} onClick={() => pickCompany(co)} className="block w-full border-b border-zinc-800/60 px-3 py-2 text-left text-sm hover:bg-[#FF6A00]/10">
                            <span className="font-medium text-white">{co.name}</span>
                            <span className="block text-xs text-zinc-500">SIRET {co.siret || "—"} · {co.address || ""}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div><Label className="text-zinc-400">Nom *</Label><Input data-testid="contact-name" className="mt-1 border-zinc-800 bg-zinc-900" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                )}
                {form.kind === "pro" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-zinc-400">SIRET</Label><Input data-testid="contact-siret" className="mt-1 border-zinc-800 bg-zinc-900" value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} /></div>
                    <div><Label className="text-zinc-400">N° TVA</Label><Input data-testid="contact-vat" className="mt-1 border-zinc-800 bg-zinc-900" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} /></div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-zinc-400">Email</Label><Input data-testid="contact-email" className="mt-1 border-zinc-800 bg-zinc-900" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                  <div><Label className="text-zinc-400">Téléphone</Label><Input className="mt-1 border-zinc-800 bg-zinc-900" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                </div>
                <div><Label className="text-zinc-400">Adresse{form.kind === "pro" ? " de facturation" : ""}</Label><AddressAutocomplete testid="contact-address" className="mt-1 border-zinc-800 bg-zinc-900" value={form.address} onChange={(v) => setForm((f) => ({ ...f, address: v }))} /></div>
                <div><Label className="text-zinc-400">Tags (séparés par des virgules)</Label><Input className="mt-1 border-zinc-800 bg-zinc-900" placeholder="VIP, Recommandé" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></div>
                <div><Label className="text-zinc-400">Notes</Label><Textarea className="mt-1 border-zinc-800 bg-zinc-900" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <Button data-testid="save-contact-btn" onClick={create} className="w-full rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]">Enregistrer</Button>
              </div>
            </DialogContent>
          </Dialog>
      </>} />

      {!loading && (
        <KpiStrip testid="contacts-kpis" items={[
          { icon: Users, label: "Contacts", value: contacts.length, tint: "#FF6A00" },
          { icon: Building2, label: "Professionnels", value: contacts.filter((c) => c.kind === "pro").length, tint: "#FFD500" },
          { icon: UserPlus, label: "Nouveaux leads", value: contacts.filter((c) => (c.pipeline_stage || "nouveau_lead") === "nouveau_lead").length, tint: "#34d399", onClick: () => setView("pipeline"), testid: "contacts-kpi-leads" },
        ]} />
      )}

      {loading && <Loader label="Chargement des contacts…" />}
      {!loading && view === "liste" && (
        <Widget testid="contacts-widget" icon={Users} title={`Annuaire (${filtered.length})`} bodyClass="p-0"
          action={(
            <div className="relative w-40 sm:w-64">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
              <Input data-testid="contact-search" placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 rounded-lg border-zinc-800 bg-zinc-950 pl-9 text-sm" />
            </div>
          )}>
          {filtered.length === 0 ? (
            <EmptyState onAdd={() => setOpen(true)} />
          ) : (
            <div>
              {filtered.map((c) => (
                <div key={c.id} data-testid={`contact-row-${c.id}`} onClick={() => navigate(`/app/contacts/${c.id}`)}
                  className="flex cursor-pointer items-center gap-4 border-b border-zinc-800 p-4 last:border-0 hover:bg-zinc-800/40">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#FF6A00]/30 to-[#FFD500]/20 text-sm font-semibold text-white">{c.name.slice(0, 1).toUpperCase()}</div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-white">{c.name}</p>
                    <p className="truncate text-sm text-zinc-500">{c.email || c.phone || "—"}</p>
                  </div>
                  <div className="hidden gap-1 sm:flex">
                    {(c.tags || []).slice(0, 2).map((t) => (
                      <span key={t} className="flex items-center gap-1 rounded-full bg-zinc-800 px-2 py-0.5 text-xs text-zinc-400"><Tag className="h-3 w-3" />{t}</span>
                    ))}
                  </div>
                  {c.kind === "pro"
                    ? <span data-testid={`contact-kind-badge-${c.id}`} className="hidden items-center gap-1 rounded-full border border-[#FF6A00]/40 bg-[#FF6A00]/10 px-2.5 py-0.5 text-xs text-[#FF6A00] sm:flex"><Building2 className="h-3 w-3" />Pro</span>
                    : <span data-testid={`contact-kind-badge-${c.id}`} className="hidden rounded-full border border-zinc-700 px-2.5 py-0.5 text-xs text-zinc-400 sm:flex">Particulier</span>}
                  <span className="rounded-full border border-zinc-700 px-2.5 py-0.5 text-xs text-zinc-400">{TYPE_LABEL[c.type]}</span>
                </div>
              ))}
            </div>
          )}
        </Widget>
      )}

      {!loading && view === "pipeline" && (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {PIPELINE.map((col) => {
            const items = contacts.filter((c) => (c.pipeline_stage || "nouveau_lead") === col.key);
            return (
              <div key={col.key} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(col.key)}
                data-testid={`kanban-col-${col.key}`}
                className="w-72 flex-shrink-0 rounded-2xl border border-zinc-800 bg-zinc-900/50 p-3">
                <div className="mb-3 flex items-center justify-between px-1">
                  <span className="text-sm font-semibold text-white">{col.label}</span>
                  <span className="rounded-full bg-zinc-800 px-2 text-xs text-zinc-400">{items.length}</span>
                </div>
                <div className="space-y-2">
                  {items.map((c) => (
                    <div key={c.id} draggable onDragStart={() => setDragId(c.id)} onClick={() => navigate(`/app/contacts/${c.id}`)}
                      data-testid={`kanban-card-${c.id}`}
                      className="cursor-grab rounded-xl border border-zinc-800 bg-zinc-950 p-3 transition-transform hover:-translate-y-0.5 active:cursor-grabbing">
                      <p className="font-medium text-white">{c.name}</p>
                      <p className="text-xs text-zinc-500">{c.email || "—"}</p>
                      {col.key === "perdu" && c.lost_reason && <p className="mt-1 text-xs text-red-400">Motif : {c.lost_reason}</p>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="py-16 text-center">
      <Users className="mx-auto h-12 w-12 text-zinc-700" />
      <p className="mt-4 font-medium text-white">Aucun contact pour l'instant</p>
      <p className="mt-1 text-sm text-zinc-500">Ajoutez votre premier client ou prospect.</p>
      <Button onClick={onAdd} className="mt-5 rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Plus className="mr-1 h-4 w-4" />Ajouter un contact</Button>
    </div>
  );
}
